import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { Updater, useImmer } from 'use-immer';

interface SelectProps {
  onSelect: (selected: any) => void;
  children: React.ReactNode;
  [key: string]: unknown;
}

interface SelectedList {
  id: string;
  index: number;
  selected: boolean;
  props: unknown;
}

const SelectList = ({ onSelect, children, ...props }: SelectProps) => {
  const [selectedList, setSelectedList] = useImmer([]);

  useEffect(() => {
    setSelectedList(
      () =>
        React.Children.map(children, (it, index) => {
          const selected = selectedList.find((x) => x?.id === it?.props?.id)?.selected;
          const hasAddNewFile = selectedList.length < children.length;

          return {
            id: it?.props?.id,
            index: +index,
            //? Update 'selected' based on conditions:
            //? 1. If 'selected' is undefined (when adding a new file not this prop), set it to true.
            //? 2. If adding a new file (selectedList.length < children.length), set 'selected' to true.
            //? 3. Otherwise, set the current 'selected' state (previously set as a boolean).
            selected: selected === undefined ? hasAddNewFile : selected,
            props: it?.props,
          };
        }) || []
    );
  }, [children.length]);

  const [lastSelect, setLastSelect] = useState<number>(null);
  const [newRange, setNewRange] = useState<boolean>(false);

  useEffect(() => {
    onSelect(
      selectedList
        .filter((it) => it.selected)
        .map((it) => {
          return {
            ...it.props?.children?.props,
            id: it.props?.id,
          };
        })
    );
  }, [onSelect, selectedList]);

  return (
    <article {...props}>
      {React.Children.map(children, (child, index) => {
        return React.cloneElement(child as React.ReactElement, {
          // id: selectedList[index].id,
          index: selectedList[index]?.index,
          // index: child?.key,
          // selected: child.props.selected,
          selectedList,
          setSelectedList,
          lastSelect,
          setLastSelect,
          newRange,
          setNewRange,
          onSelect,
        });
      })}
    </article>
  );
};

const SelectItem = ({
  children,
  index,
  className = '',
  selectedList,
  setSelectedList,
  lastSelect,
  setLastSelect,
  newRange,
  setNewRange,
  // onSelect,

  ...props
}: {
  children: React.ReactNode;
  id: string;
  index?: number;
  className?: string;
  selectedList?: SelectedList[];
  setSelectedList?: Updater<SelectedList[]>;
  lastSelect?: number;
  setLastSelect?: React.Dispatch<React.SetStateAction<number>>;
  newRange?: boolean;
  setNewRange?: React.Dispatch<React.SetStateAction<boolean>>;
  [key: string]: unknown;
}) => {
  const changeSelectList = useCallback(
    (min: number, max: number) => {
      setSelectedList((draft) => {
        draft.forEach((it, i) => {
          if (i >= min && i <= max) {
            draft[i].selected = true;

            return;
          }
          draft[i].selected = false;
        });
      });
    },
    [setSelectedList]
  );

  const handleClick = useCallback(
    (e: MouseEvent) => {
      const selectedIds = Object.keys(selectedList)
        .filter((it) => selectedList[it].selected)
        .map((it) => +it);

      if (e.shiftKey && e.ctrlKey) {
        const [startIndex, endIndex] = [selectedIds[0], selectedIds[selectedIds.length - 1]];

        //? If have range and single select, select all from [lastSelect, index] and old selected range
        if (newRange) {
          const min = Math.min(lastSelect, index);
          const max = Math.max(lastSelect, index);

          setSelectedList((draft) => {
            draft.forEach((it, i) => {
              if (i >= min && i <= max) {
                draft[i].selected = true;

                return;
              }
            });
          });
          setNewRange(false);

          return;
        } else {
          //? If have range and not have single select, merge range from index and old selected range
          if (index < startIndex) {
            changeSelectList(index, endIndex);

            return;
          } else if (index > endIndex) {
            changeSelectList(startIndex, index);

            return;
          }
        }
      } else if (e.shiftKey) {
        switch (selectedIds.length) {
          case 1:
            {
              const selectedIndex = selectedList.findIndex((it) => it.selected);
              const min = Math.min(selectedIndex, index);
              const max = Math.max(selectedIndex, index);

              //? If only one item is selected, find what is the min and max index between selected item and clicked item, then select all from [min, max]
              changeSelectList(min, max);
            }
            break;
          case 0:
            //? If no item is selected, select all from [0, index]
            setSelectedList((draft) => {
              draft.forEach((it, i) => {
                if (i <= index) {
                  draft[i].selected = true;

                  return;
                }
                draft[i].selected = false;
              });
            });
            break;
          default:
            {
              const [startIndex, endIndex] = [selectedIds[0], selectedIds[selectedIds.length - 1]];
              let isRange = true;

              for (let i = startIndex; i <= endIndex; i++) {
                if (!selectedList[i].selected) {
                  isRange = false;
                  break;
                }
              }
              //? When select not sequential range, set range from [lastSelect, index]
              if (!isRange) {
                const [min, max] = [Math.min(lastSelect, index), Math.max(lastSelect, index)];

                changeSelectList(min, max);

                return;
              }

              //? When select range, clicked item < startRange: deselect all from [startIndex, endIndex], now startRange is new endRange => [index, startIndex]
              if (isRange && index < startIndex) {
                changeSelectList(index, startIndex);

                return;
              }

              //? When select range, clicked item > endRange: deselect all from [startIndex, endIndex], now endRange is new startRange => [endIndex, index]
              if (isRange && index > endIndex) {
                changeSelectList(endIndex, index);

                return;
              }
            }
            break;
        }
      } else {
        setSelectedList((draft) => {
          draft[index].selected = !draft[index].selected;
        });
        setNewRange(true);

        // setSelectedList((draft) => {
        //   Object.keys(draft).forEach((it, i) => {
        //     if (i === index) {
        //       //? When select range, then click item in range: deselect all except clicked item | When only one item is selected: deselect all, then select clicked item
        //       draft[i].selected =
        //         selectedIds.length > 1 && draft[i].selected ? true : !draft[i].selected;

        //       return;
        //     }
        //     draft[i].selected = false;
        //   });
        // });
      }

      setLastSelect(index);

      // onSelect(selectedList[index]?.props?.children?.props);
    },
    [
      changeSelectList,
      index,
      lastSelect,
      newRange,
      selectedList,
      setLastSelect,
      setNewRange,
      setSelectedList,
    ]
  );

  return (
    <div
      className={classNames(
        'flex cursor-pointer select-none items-center justify-between',
        className,
        {
          'outline outline-1 outline-[#2799b0]': selectedList[index]?.selected,
        }
      )}
      onClick={handleClick as any}
      {...props}
    >
      {children}
    </div>
  );
};

SelectList.Item = SelectItem;

export default SelectList;
